import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Link2,
  Star,
} from "lucide-react";
import moment from "moment-timezone";
import Image from "next/image";
import ListModal from "src/components/dashboard/listModal";
import { FlashingValue } from "src/common/FlashingValue";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import BriefCaseFilledIcon from "src/assets/images/Briefcase-Fiilled.svg";
import Briefcase from "src/assets/images/Briefcase.svg";
import InvestmentIcon from "src/assets/images/investment-analysis-icon.svg";
import WatchlistFilledIcon from "src/assets/images/WatchList-Filled.svg";
import WatchlistIcon from "src/assets/images/Watchlist.svg";
import ConfirmationModal from "src/common/ConfirmationModal";
import FreeTrialRestrictionModal from "src/common/freeTrialRestrictionModal";
import { useSocket } from "src/components/SocketContext";
import PastBlocksSkeleton from "src/components/skeleton/PastBlocksSkeleton";
import {
  fetchWrapper,
  getHotMonths,
  OrderBlockForPastDays,
  StockTableData,
  Watchlist,
} from "src/helpers";
import { StockSearchInput, useStockSearch } from "src/hooks/useStockSearchHook";
import {
  formatDate,
  getLocalStorageUser,
} from "src/helpers/api/sharedFunction";
import { formatCurrencyWithLocalString } from "src/helpers/numberFormatter";
import { alertService } from "src/services";
import hotFlame from "src/assets/images/fire.png";
import SupplyDemandTable from "./SupplyDemandTable";
import DialogModal from "src/common/DialogModal";
import SnowflakeIcon from "src/common/SnowflakeIcon";

type SortDirection = "asc" | "desc" | null;
export type RestrictedAction = "portfolio" | "watchlist";

type SortConfig = {
  key: string;
  direction: SortDirection;
};

type LtpMessage = {
  symbol_name: string;
  ltp: string;
  close: string;
  high?: string;
  symbol_token?: string;
  ltp_date?: string;
};

type ExtendedOrderBlock = OrderBlockForPastDays &
  Partial<{
    ltp: number;
    close: number;
    lastUpdated: number;
    added_to_portfolio: boolean;
    added_to_watchlist: boolean;
  }>;

interface PastBlocksTableProps {
  data: OrderBlockForPastDays[];
  onRowSelect?: (row: OrderBlockForPastDays) => void;
  selectedRow?: { symbol: string; ob_date?: string };
  className?: string;
  setPastderivative30: Dispatch<SetStateAction<boolean>>;
  pastDerivative30: boolean;
  onWatchlistUpdate?: (symbol: string) => void;
  onWatchlistRemove?: (symbol: string) => void;
  onPortfolioUpdate?: (symbol: string) => void;
  loading?: boolean;
}

interface PastBlocksTableRowProps {
  row: ExtendedOrderBlock;
  isSelected: boolean;
  onRowSelect: (row: OrderBlockForPastDays) => void;
  onPortfolioClick: (e: React.MouseEvent, row: ExtendedOrderBlock) => void;
  onWatchlistClick: (e: React.MouseEvent, row: ExtendedOrderBlock) => void;
  isPortfolioLimitReached: boolean;
  isWatchlistLimitReached: boolean;
  livePrice?: { ltp: number; close: number; lastUpdated: Date };
}

const PastBlocksTableRow = React.memo(function PastBlocksTableRow({
  row,
  isSelected,
  onRowSelect,
  onPortfolioClick,
  onWatchlistClick,
  isPortfolioLimitReached,
  isWatchlistLimitReached,
  livePrice,
}: PastBlocksTableRowProps) {
  const rowSymbol = row.symbol.replace(/\.NS$/, "");
  const currentLtp = livePrice?.ltp ?? row.ltp;
  const currentClose = livePrice?.close ?? row.close;
  const lastUpdated = livePrice?.lastUpdated;

  return (
    <tr
      id={`row-${rowSymbol}-past`}
      className={`border-b border-[#d0d4e4] dark:border-[#394564] hover:bg-gray-700/20 transition-colors cursor-pointer ${
        isSelected
          ? "border-l-[2px] border-l-[#00a85f] dark:border-l-[#00a85f] bg-[#edf1fc] dark:bg-[#00225580]"
          : ""
      }`}
      onClick={() => onRowSelect(row)}
    >
      <td className="min-w-[100px] py-2 px-2 text-xs dark:group-hover:bg-[#394564]/50 transition-colors duration-200">
        {formatDate(row.ob_date)}
      </td>
      <td className="py-2 px-2 text-xs dark:group-hover:bg-[#394564]/50 transition-colors duration-200 min-w-[120px]">
        <div className="flex items-center gap-1">
          <span className="w-4 h-4 flex items-center gap-1 flex-shrink-0">
            {row?.hot_flame && (
              <div className="relative group">
                <Image
                  src={hotFlame}
                  alt="hot flame"
                  className="w-4 h-4 transition-transform dark:group-hover:scale-110"
                />
                <div className="absolute z-10 hidden w-80 top-[-60px] translate-y-0 sm:w-max sm:top-1/2 sm:-translate-y-1/2 group-hover:block bg-[#30324e] text-white text-sm p-2 rounded-md shadow-lg border border-[#394564] left-full ml-0 sm:ml-2">
                  This stock is identified as a high performer based on its
                  2-months performance trend. (
                  {getHotMonths(moment(row.ob_date)).join(", ")})
                  <div className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-r-4 border-r-[#30324e]"></div>
                </div>
              </div>
            )}
            {row?.snow_flake && <SnowflakeIcon />}
          </span>
          <span>{rowSymbol}</span>
        </div>
      </td>
      <td className="py-2 px-2 text-xs text-right min-w-[150px]">
        <FlashingValue
          value={
            currentLtp
              ? formatCurrencyWithLocalString(currentLtp.toFixed(2))
              : "--"
          }
          isPositive={
            currentLtp && currentClose && currentLtp - currentClose === 0
              ? null
              : !(currentLtp && currentClose && currentLtp - currentClose < 0)
          }
          flashTrigger={lastUpdated}
          className="text-xs font-normal"
        />{" "}
        &nbsp;
        <span
          className={`${
            currentLtp && currentClose && currentLtp - currentClose === 0
              ? "text-gray-900 dark:text-white"
              : currentLtp && currentClose && currentLtp - currentClose < 0
                ? "dark:text-red-400 text-red-600"
                : "dark:text-green-400 text-green-600"
          }`}
        >
          (
          {currentClose && currentLtp
            ? (((currentLtp - currentClose) / currentClose) * 100).toFixed(2)
            : "0.00"}
          %)
        </span>
      </td>
      <td className="py-1 px-4 sm:px-6 text-xs dark:group-hover:bg-[#394564]/50 transition-colors duration-200 text-center">
        <div className="flex gap-2 items-center justify-center">
          <span
            className={`px-0 py-0 rounded-full text-xs font-medium ${
              row.side === "supply"
                ? "text-red-800 dark:text-red-300"
                : "text-green-800 dark:text-green-300"
            }`}
          >
            {row.side === "supply" ? <ChevronDown /> : <ChevronUp />}
          </span>
          <span
            className={`px-1 py-0.5 rounded-full text-xs font-medium min-w-[80px] ${
              row.side === "supply"
                ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
            }`}
          >
            {row.side}
          </span>
        </div>
      </td>
      <td className="py-2 px-2 text-xs dark:group-hover:bg-[#394564]/50 transition-colors duration-200 text-center">
        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300 text-center">
          {row.score}
        </span>
      </td>
      <td className="py-2 px-2 text-center">
        <Star
          className={`w-4 h-4 mx-auto ${
            row.feat_displacement === true
              ? "text-yellow-400 fill-current"
              : "text-gray-300 dark:text-gray-600"
          }`}
        />
      </td>
      <td className="py-2 px-2 text-center">
        <Star
          className={`w-4 h-4 mx-auto ${
            row.feat_bos === true
              ? "text-yellow-400 fill-current"
              : "text-gray-300 dark:text-gray-600"
          }`}
        />
      </td>
      <td className="py-2 px-2 text-center">
        <Star
          className={`w-4 h-4 mx-auto ${
            row.feat_fvg === true
              ? "text-yellow-400 fill-current"
              : "text-gray-300 dark:text-gray-600"
          }`}
        />
      </td>
      <td className="py-2 px-2 text-center">
        <Star
          className={`w-4 h-4 mx-auto ${
            row.feat_retest === true
              ? "text-yellow-400 fill-current"
              : "text-gray-300 dark:text-gray-600"
          }`}
        />
      </td>
      <td className="py-2 px-2 text-center">
        <Star
          className={`w-4 h-4 mx-auto ${
            row.feat_sweep === true
              ? "text-yellow-400 fill-current"
              : "text-gray-300 dark:text-gray-600"
          }`}
        />
      </td>
      <td className="py-2 px-2 text-center">
        <Star
          className={`w-4 h-4 mx-auto ${
            row.feat_voltrend === true
              ? "text-yellow-400 fill-current"
              : "text-gray-300 dark:text-gray-600"
          }`}
        />
      </td>
      <td className="dark:group-hover:bg-[#394564]/50 transition-colors duration-200">
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={(e) => onPortfolioClick(e, row)}
            title={
              isPortfolioLimitReached && !row?.added_to_portfolio
                ? "Free trial limit reached"
                : "Add to Portfolio"
            }
            className={`rounded-full p-1 transition-all duration-200 ${
              isPortfolioLimitReached && !row?.added_to_portfolio
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer"
            }`}
          >
            <Image
              src={row?.added_to_portfolio ? BriefCaseFilledIcon : Briefcase}
              alt="Briefcase"
              style={{
                height: "auto",
                filter: row?.added_to_portfolio
                  ? "invert(62%) sepia(66%) saturate(1200%) hue-rotate(350deg) brightness(100%) contrast(95%)"
                  : "",
              }}
              className={`w-5 h-5 transition-all duration-200 ${
                row?.added_to_portfolio
                  ? "filter-orange"
                  : "filter invert dark:invert-0 dark:brightness-100"
              }`}
            />
          </button>
          <button
            onClick={(e) => onWatchlistClick(e, row)}
            title={
              isWatchlistLimitReached && !row?.added_to_watchlist
                ? "Free trial limit reached"
                : "Add to Watchlist"
            }
            className={`rounded-full p-1 transition-all duration-200 ${
              isWatchlistLimitReached && !row?.added_to_watchlist
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer"
            }`}
          >
            <Image
              src={
                row?.added_to_watchlist ? WatchlistFilledIcon : WatchlistIcon
              }
              alt="Watchlist"
              style={{
                height: "auto",
                filter: row?.added_to_watchlist
                  ? "invert(62%) sepia(66%) saturate(1200%) hue-rotate(350deg) brightness(100%) contrast(95%)"
                  : "",
              }}
              className={`w-5 h-5 transition-all duration-200 ${
                row?.added_to_watchlist
                  ? "filter-orange"
                  : "filter invert dark:invert-0 dark:brightness-100"
              }`}
            />
          </button>
        </div>
      </td>
    </tr>
  );
});

const PastBlocksTable = ({
  data,
  onRowSelect,
  selectedRow,
  className = "",
  pastDerivative30,
  setPastderivative30,
  onWatchlistUpdate,
  onWatchlistRemove,
  onPortfolioUpdate,
  loading = false,
}: PastBlocksTableProps) => {
  const [prevData, setPrevData] = useState<OrderBlockForPastDays[]>(data);
  const [records, setLocalRecords] = useState<ExtendedOrderBlock[]>(data);

  if (data !== prevData) {
    setPrevData(data);
    setLocalRecords(data);
  }

  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [supplyDemandModalOpen, setSupplyDemandModalOpen] = useState(false);
  const [watchlist, setWatchlist] = useState<Watchlist>({});
  const [selectedStock, setSelectedStock] = useState<StockTableData | null>(
    null,
  );
  const [freeTrialRestrictModal, setFreeTrialRestrictModal] = useState(false);
  const [
    freeTrialRestrictModalPastOrderBlock,
    setFreeTrialRestrictModalPastOrderBlock,
  ] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState("");
  const [quantity, setQuantity] = useState<number>(1);
  const [buyPrice, setBuyPrice] = useState<number>(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "",
    direction: null,
  });
  const [confirmationModal, setConfirmationModal] = useState(false);
  const {
    searchInput,
    filteredSymbols,
    showDropdown,
    setShowDropdown,
    onSearchChange,
    handleSuggestionClick,
  } = useStockSearch(
    records,
    selectedRow,
    onRowSelect,
    "past-blocks-search-dropdown",
    "past",
  );

  const user = getLocalStorageUser();
  const token = user.token;

  const onRowSelectRef = useRef(onRowSelect);
  const onWatchlistUpdateRef = useRef(onWatchlistUpdate);
  const onWatchlistRemoveRef = useRef(onWatchlistRemove);
  const onPortfolioUpdateRef = useRef(onPortfolioUpdate);

  useLayoutEffect(() => {
    onRowSelectRef.current = onRowSelect;
    onWatchlistUpdateRef.current = onWatchlistUpdate;
    onWatchlistRemoveRef.current = onWatchlistRemove;
    onPortfolioUpdateRef.current = onPortfolioUpdate;
  }, [onRowSelect, onWatchlistUpdate, onWatchlistRemove, onPortfolioUpdate]);

  const stableOnRowSelect = useCallback((row: OrderBlockForPastDays) => {
    onRowSelectRef.current?.(row);
  }, []);

  const portfolioCount = Number(user?.portfolio_count ?? 0);
  const watchlistCount = Number(user?.watchlist_count ?? 0);
  const stockLimit = Number(process.env.NEXT_PUBLIC_STOCK_COUNT_LIMIT);
  const isPortfolioLimitReached = !!(
    user?.is_trial && portfolioCount >= stockLimit
  );
  const isWatchlistLimitReached = !!(
    user?.is_trial && watchlistCount >= stockLimit
  );

  const limitsRef = useRef({
    portfolio: isPortfolioLimitReached,
    watchlist: isWatchlistLimitReached,
  });

  useLayoutEffect(() => {
    limitsRef.current = {
      portfolio: isPortfolioLimitReached,
      watchlist: isWatchlistLimitReached,
    };
  }, [isPortfolioLimitReached, isWatchlistLimitReached]);

  const updatedStock = useCallback(
    (stockSymbol: string, isWatchlist?: boolean) => {
      if (!stockSymbol) return;
      const normalizedSymbol = stockSymbol.replace(/\.NS$/, "");
      setLocalRecords((prevRecords) =>
        prevRecords.map((item) =>
          item.symbol.replace(/\.NS$/, "") === normalizedSymbol
            ? {
                ...item,
                [isWatchlist ? "added_to_watchlist" : "added_to_portfolio"]:
                  true,
              }
            : item,
        ),
      );
      if (isWatchlist) {
        onWatchlistUpdateRef.current?.(stockSymbol);
      } else {
        onPortfolioUpdateRef.current?.(stockSymbol);
      }
    },
    [],
  );

  const handleResetField = useCallback(() => {
    setWatchlist({ symbol: "", is_recurring: "" });
    setNotes("");
    setQuantity(0);
    setBuyPrice(0);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const openModal = useCallback((stock: any) => {
    setSelectedDate(new Date(stock.ob_date));
    setSelectedStock(stock);
    setQuantity(1);
    setBuyPrice(Number(stock.ltp));
    setNotes("");
    setIsModalOpen(true);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const openWatchlistModal = useCallback((stock: any) => {
    setSelectedStock(stock);
    if (!stock?.added_to_watchlist) {
      setIsOpen(true);
    } else {
      setConfirmationModal(true);
    }
    setWatchlist({
      symbol: stock.symbol,
      is_recurring: "",
    });
  }, []);

  const handlePortfolioClick = useCallback(
    (e: React.MouseEvent, row: ExtendedOrderBlock) => {
      e.stopPropagation();
      const rowSymbol = row.symbol.replace(/\.NS$/, "");
      const stock = { ...row, symbol: rowSymbol };
      if (row.added_to_portfolio || !limitsRef.current.portfolio) {
        openModal(stock);
      } else {
        setFreeTrialRestrictModal(true);
      }
    },
    [openModal],
  );

  const handleWatchlistClick = useCallback(
    (e: React.MouseEvent, row: ExtendedOrderBlock) => {
      e.stopPropagation();
      const rowSymbol = row.symbol.replace(/\.NS$/, "");
      const stock = { ...row, symbol: rowSymbol };
      if (row.added_to_watchlist || !limitsRef.current.watchlist) {
        openWatchlistModal(stock);
      } else {
        setFreeTrialRestrictModal(true);
      }
    },
    [openWatchlistModal],
  );

  const { emit, on, off, isConnected } = useSocket();
  const updateBufferRef = useRef<Map<string, { ltp: number; close: number }>>(
    new Map(),
  );
  const [livePrices, setLivePrices] = useState<
    Record<string, { ltp: number; close: number; lastUpdated: Date }>
  >({});

  useEffect(() => {
    const flushInterval = setInterval(() => {
      const buffer = updateBufferRef.current;
      if (buffer.size === 0) return;

      setLivePrices((prev) => {
        const next = { ...prev };
        let hasChanges = false;
        buffer.forEach(({ ltp, close }, symbol) => {
          if (next[symbol]?.ltp !== ltp) {
            next[symbol] = { ltp, close, lastUpdated: new Date() };
            hasChanges = true;
          }
        });
        return hasChanges ? next : prev;
      });

      buffer.clear();
    }, 500);

    return () => clearInterval(flushInterval);
  }, []);

  const handleStockUpdate = useCallback((message: LtpMessage[]) => {
    for (const m of message) {
      const ltp = parseFloat(m.ltp);
      const close = parseFloat(m.close);
      if (!isNaN(ltp)) {
        updateBufferRef.current.set(m.symbol_name, { ltp, close });
      }
    }
  }, []);

  const symbolsKey = useMemo(
    () =>
      (data ?? [])
        .map((r) => r.symbol.replace(/\.NS$/, ""))
        .filter((symbol, index, self) => self.indexOf(symbol) === index)
        .sort()
        .join(","),
    [data],
  );

  useEffect(() => {
    if (!symbolsKey || !isConnected) return;
    const symbols = symbolsKey.split(",");
    on("stock_update", handleStockUpdate);
    emit("subscribe_stock", { symbol: symbols });

    return () => {
      off("stock_update", handleStockUpdate);
      emit("unsubscribe_stock", { symbol: symbols });
    };
  }, [symbolsKey, isConnected, on, off, emit, handleStockUpdate]);

  const sortedData = useMemo(() => {
    const sortableData = [...records];
    if (!sortConfig.key || !sortConfig.direction) return sortableData;

    return sortableData.sort((a, b) => {
      const { key, direction } = sortConfig;

      if (key === "score") {
        return direction === "asc"
          ? Number(a.score) - Number(b.score)
          : Number(b.score) - Number(a.score);
      }

      if (key === "percentage") {
        const aPercent =
          a.ltp && a.close ? ((a.ltp - a.close) / a.close) * 100 : 0;
        const bPercent =
          b.ltp && b.close ? ((b.ltp - b.close) / b.close) * 100 : 0;
        return direction === "asc" ? aPercent - bPercent : bPercent - aPercent;
      }

      const aValue = a[key as keyof OrderBlockForPastDays];
      const bValue = b[key as keyof OrderBlockForPastDays];

      if (aValue == null || bValue == null) return 0;
      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [records, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        let newDirection: SortDirection = null;
        if (prev.direction === null) newDirection = "desc";
        else if (prev.direction === "desc") newDirection = "asc";
        else if (prev.direction === "asc") newDirection = null;

        return { key, direction: newDirection };
      }

      return { key, direction: "desc" };
    });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key)
      return (
        <ArrowUpDown className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
      );
    if (sortConfig.direction === "asc")
      return <ArrowUp className="w-3 h-3 ml-1" />;
    if (sortConfig.direction === "desc")
      return <ArrowDown className="w-3 h-3 ml-1" />;
    return (
      <ArrowUpDown className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
    );
  };

  const handleToggleButton = () => setPastderivative30(!pastDerivative30);

  const handleOnClick = async (response: string) => {
    if (response === "Delete") {
      if (!token) {
        alertService.error("Authentication token not found");
        return;
      }
      try {
        const stockName = selectedStock?.symbol.replace(/\.NS$/, "");
        await fetchWrapper.delete(
          `/api/users/watchlists/delete?stock_name=${stockName}`,
          undefined,
          { Authorization: `Bearer ${token}` },
        );

        const storedUser = getLocalStorageUser();

        if (storedUser) {
          const updatedUser = {
            ...storedUser,
            watchlist_count: Math.max(
              Number(storedUser.watchlist_count ?? 0) - 1,
              0,
            ),
          };

          localStorage.setItem("BetaUser", JSON.stringify(updatedUser));
        }

        setLocalRecords((prev) =>
          prev.map((r) =>
            r.symbol.replace(/\.NS$/, "") === stockName
              ? { ...r, added_to_watchlist: false }
              : r,
          ),
        );

        onWatchlistRemoveRef.current?.(stockName as string);

        setConfirmationModal(false);
      } catch (error) {
        alertService.error("Failed to delete watchlist item");
        console.error("Delete error:", error);
      }
    } else {
      setConfirmationModal(false);
    }
  };

  return (
    <div
      className={`border border-gray-200 dark:border-gray-700 shadow-sm rounded-lg w-full ${className}`}
    >
      <style>{`
      :root {
        --flash-bg: #fde047;
        --flash-text: #000000;
      }
      .dark {
        --flash-bg: #eab308;
        --flash-text: #000000;
      }
     `}</style>
      <div className="flex items-center justify-between p-2 h-10">
        <div className="flex gap-2">
          <h3 className="text-sm font-semibold text-black dark:text-white flex items-center">
            Order Blocks in Past 30 Days
          </h3>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (user?.is_trial) {
                setFreeTrialRestrictModalPastOrderBlock(true);
              } else {
                setSupplyDemandModalOpen(true);
              }
            }}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors cursor-pointer"
            title="View Supply & Demand Details"
          >
            <Link2 className="w-4 h-4 transition-colors text-primary hover:text-primary/80" />
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <StockSearchInput
            searchInput={searchInput}
            onSearchChange={onSearchChange}
            setShowDropdown={setShowDropdown}
            showDropdown={showDropdown}
            filteredSymbols={filteredSymbols}
            records={records}
            handleSuggestionClick={handleSuggestionClick}
            dropdownId="past-blocks-search-dropdown"
          />
          <span className="text-xs text-gray-600 dark:text-gray-300">
            Derivatives
          </span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={pastDerivative30}
              onChange={handleToggleButton}
            />
            <div
              className="w-9 h-5 bg-gray-200 rounded-full peer-focus:outline-none dark:bg-gray-700
        peer-checked:bg-blue-600
        after:content-[''] after:absolute after:top-[2px] after:left-[2px]
        after:bg-white after:border-gray-300 after:border after:rounded-full
        after:h-4 after:w-4 after:transition-all
        peer-checked:after:translate-x-full peer-checked:after:border-white"
            ></div>
          </label>
          <span className="text-xs text-gray-600 dark:text-gray-300">
            Non-Derivatives
          </span>
        </div>
      </div>
      <div className="overflow-x-auto relative h-[calc(100%-40px)] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f4f5f8] dark:bg-[#30324e] sticky top-0 z-10">
            <tr>
              <th
                className="py-2 px-2 text-start text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none"
                onClick={() => handleSort("ob_date")}
              >
                <div className="flex items-center">
                  OB Date
                  {getSortIcon("ob_date")}
                </div>
              </th>
              <th
                className="py-2 px-2 text-start text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none min-w-[120px]"
                onClick={() => handleSort("symbol")}
              >
                <div className="flex items-center gap-1">
                  <span className="w-4 h-4 flex-shrink-0"></span>
                  <span>Symbol</span>
                  {getSortIcon("symbol")}
                </div>
              </th>
              <th
                className="py-2 px-2 text-start text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none"
                onClick={() => handleSort("percentage")}
              >
                <div className="flex justify-end items-center">
                  Price
                  {getSortIcon("percentage")}
                </div>
              </th>
              <th
                className="py-2 px-4 sm:px-6 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none"
                onClick={() => handleSort("side")}
              >
                <div className="flex items-center justify-center">
                  Direction
                  {getSortIcon("side")}
                </div>
              </th>
              <th
                className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree group cursor-pointer select-none"
                onClick={() => handleSort("score")}
              >
                <div className="flex items-center justify-center">
                  Score
                  {getSortIcon("score")}
                </div>
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                Displaced
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                BoS
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                FvG
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                ReTest
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                Sweep
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                Vol.Trend
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <PastBlocksSkeleton />
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={12} className="py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
                      No records found
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-500">
                      Try adjusting toggle
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => {
                const rowSymbol = row.symbol.replace(/\.NS$/, "");
                return (
                  <PastBlocksTableRow
                    key={`${rowSymbol}-${row.ob_date}-${row.side}-${index}`}
                    row={row}
                    isSelected={
                      selectedRow?.symbol === rowSymbol &&
                      selectedRow?.ob_date === row.ob_date
                    }
                    onRowSelect={stableOnRowSelect}
                    onPortfolioClick={handlePortfolioClick}
                    onWatchlistClick={handleWatchlistClick}
                    isPortfolioLimitReached={isPortfolioLimitReached}
                    isWatchlistLimitReached={isWatchlistLimitReached}
                    livePrice={livePrices[rowSymbol]}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {isModalOpen && selectedStock && (
        <DialogModal
          title=""
          message={
            <ListModal
              heading="Add to Portfolio"
              investmentImg={InvestmentIcon}
              investmentAltText={"Investment"}
              notes={notes}
              selectedStock={selectedStock}
              quantity={quantity}
              updatedStock={updatedStock}
              setQuantity={setQuantity}
              buyPrice={buyPrice}
              setBuyPrice={setBuyPrice}
              setNotes={setNotes}
              isDashboard={true}
              selected_date={selectedDate!}
              getData={async () => {
                setLocalRecords((prev) =>
                  prev.map((r) =>
                    r.symbol.replace(/\.NS$/, "") === selectedStock?.symbol
                      ? { ...r, added_to_watchlist: true }
                      : r,
                  ),
                );
              }}
              handleOnClick={() => {
                setIsModalOpen(false);
                handleResetField();
              }}
              isModalOpen={isModalOpen}
            />
          }
          isModalList={true}
          show={isModalOpen}
          setShow={setIsModalOpen}
          handleOnClick={() => {
            setIsModalOpen(false);
            handleResetField();
          }}
          noBackdrop={true}
        />
      )}
      {isOpen && selectedStock && (
        <DialogModal
          title=""
          message={
            <ListModal
              heading="Add to Watchlist"
              isDashboard={true}
              isWatchlist={true}
              notes={notes}
              updatedStock={updatedStock}
              selectedStock={selectedStock}
              setNotes={setNotes}
              handleOnClick={() => {
                setIsOpen(false);
                handleResetField();
              }}
              watchlist={watchlist}
              setWatchlist={setWatchlist}
              getData={async () => {
                setLocalRecords((prev) =>
                  prev.map((r) =>
                    r.symbol.replace(/\.NS$/, "") === selectedStock?.symbol
                      ? { ...r, added_to_watchlist: true }
                      : r,
                  ),
                );
              }}
            />
          }
          isModalList={true}
          show={isOpen}
          setShow={setIsOpen}
          handleOnClick={() => {
            setIsOpen(false);
            handleResetField();
          }}
          noBackdrop={true}
        />
      )}
      {confirmationModal && (
        <ConfirmationModal
          title={"Remove Stock from WatchList"}
          message={"Are you sure you want to remove stock from WatchList?"}
          show={confirmationModal}
          setShow={setConfirmationModal}
          isDeleteModal={true}
          handleOnClick={handleOnClick}
        />
      )}
      <FreeTrialRestrictionModal
        isOpen={freeTrialRestrictModal}
        onClose={() => setFreeTrialRestrictModal(false)}
        variant="addStock"
      />
      <FreeTrialRestrictionModal
        isOpen={freeTrialRestrictModalPastOrderBlock}
        onClose={() => setFreeTrialRestrictModalPastOrderBlock(false)}
        variant="orderBlock"
      />
      {supplyDemandModalOpen && (
        <DialogModal
          title="Supply & Demand Analysis"
          message={
            <SupplyDemandTable type="past" derivativeType={!pastDerivative30} />
          }
          show={supplyDemandModalOpen}
          setShow={setSupplyDemandModalOpen}
          handleOnClick={() => setSupplyDemandModalOpen(false)}
          maxWidth="max-w-4xl"
        />
      )}
    </div>
  );
};

export default PastBlocksTable;
